/**
 * ⭐⭐ `R2-UI1` · **El panel «Agregar plantilla».** `R2-F2-CA-17`…`CA-31`,
 * `CA-36`, `PRD_R2.md:865-908`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ① ESTE PANEL LO DIBUJA LA DIRECCIÓN, NO UN BOTÓN — `CA-17` / `CA-19`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-17` pide que «Agregar plantilla» lleve a `#/brazos/<brazo>/plantillas/agregar`
 * y `CA-19` que esa dirección **sobreviva a la recarga**. Las dos exigen que el
 * segmento exista en el MARCO, y **`R2-UI1` lo agrega** (costura 2 de la etapa):
 *
 *   · `src/rutas.ts` §`SEGMENTOS` .............. `agregarPlantilla: "plantillas/agregar"`
 *   · `src/rutas.ts` §`PANTALLAS_DEL_BRAZO` .... `SEGMENTOS.agregarPlantilla`
 *   · `src/App.tsx` ............................ una `<Route>` hermana con `panelAgregar`
 *
 * ⛔⛔ **La salida cómoda —abrir el panel con un `useState` y no tocar el marco—
 * está DESCARTADA y no se tomó.** Cumpliría `CA-18`, `CA-20`…`CA-31` y dejaría
 * `CA-17` y `CA-19` **silenciosamente en rojo**: el panel se vería terminado, la
 * recarga lo cerraría, y nadie tendría un instrumento que lo dijera.
 *
 * ⇒ **este componente no tiene estado de apertura y no puede tenerlo**: se monta
 * porque la URL lo dice. Es lo único que hace `CA-19` verdadero, y es la razón por
 * la que «abierto» no es una prop booleana que alguien pueda mover desde afuera.
 *
 * ⛔⛔ **Y deroga `R1.1-F2-CA-104`** (`PRD_R1.1.md:441`), que pedía que esa
 * dirección mostrara «Esta pantalla no existe»: era correcto mientras el panel
 * fuera maqueta. Su caso se REESCRIBIÓ —no se borró— en
 * `pruebas/r11-pantallas-del-brazo.test.tsx` (A11, `ADR-0082`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ② QUÉ SE OFRECE, Y LAS TRES RESTAS QUE NO SON UNA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Regla 5 de `R2-F2`. Se ofrece lo que cumple **las tres a la vez**:
 *
 *   ① está en el CATÁLOGO —o sea, la última sincronización la trajo— (`CA-24`);
 *   ② su `approvalStatus` es `ESTADO_APROBADA_EN_META` (`CA-22`);
 *   ③ su terna `(name, language)` NO está en la lista del brazo (`CA-23`).
 *
 * ⚠️ **① no es «existe en Meta».** `CA-24` lo dice con un caso: una plantilla
 * aprobada en Meta pero **creada después de la última sincronización** no se
 * ofrece, porque este producto no le pregunta a Meta desde el navegador
 * (`borde-plantillas.ts` §①). Lo que se ofrece sale de la foto, y la foto tiene
 * fecha — que es lo que la ayuda de `CA-20` dice con todas las letras.
 *
 * ⛔ **La resta ③ es por `(name, language)` y no por `name`.** La misma plantilla
 * existe una vez por idioma del lado de Meta, así que restar por nombre sacaría de
 * la oferta el `es_CL` de una plantilla porque su `en_US` ya está listado.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐ ③ EL BOTÓN DESHABILITADO DICE DOS COSAS DISTINTAS, Y SE LEEN DISTINTO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-26` (no hay nada elegido) y `CA-31` (no hay nada para elegir) dejan el botón
 * igual de deshabilitado, y **son dos situaciones opuestas**: en la primera el
 * operador tiene que elegir, en la segunda tiene que ir a sincronizar. Por eso
 * `CA-31` trae un texto propio —*«Si creaste una en Meta, sincroniza primero»*— y
 * no se conforma con la lista vacía.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import { ESTADO_APROBADA_EN_META } from "./borde-plantillas.ts"
import type { FilaParaEnvios, PlantillaDelBrazo } from "./borde-plantillas.ts"
import {
  botonParaMostrar,
  categoriaEnEspanol,
  estadoEnEspanol,
  variablesParaMostrar,
} from "./veredicto.ts"

/** La terna que identifica una plantilla, serializada para comparar. */
function terna(name: string, language: string): string {
  return `${name}\u0000${language}`
}

/**
 * Lo que el panel ofrece. Ver el bloque ② del encabezado: **las tres restas, cada
 * una en su renglón**, para que se pueda ver cuál falta si alguna se cae.
 *
 * ⛔ **No se ordena y no se deduplica.** El orden es el del catálogo; inventarle
 * uno alfabético acá lo haría distinto del de la tabla de atrás, sobre los mismos
 * nombres.
 */
export function plantillasQueSeOfrecen(
  catalogoDelBrazo: readonly PlantillaDelBrazo[],
  filas: readonly FilaParaEnvios[],
): readonly PlantillaDelBrazo[] {
  const yaListadas = new Set(filas.map((fila) => terna(fila.name, fila.language)))
  return catalogoDelBrazo.filter(
    (plantilla) =>
      plantilla.approvalStatus === ESTADO_APROBADA_EN_META &&
      !yaListadas.has(terna(plantilla.name, plantilla.language)),
  )
}

export function PanelAgregar({
  sello,
  ofrecidas,
  enCurso,
  error,
  onCancelar,
  onAgregar,
}: {
  /** El nombre del brazo, ya armado («KAI · Brazo Híbrido»). */
  readonly sello: string
  readonly ofrecidas: readonly PlantillaDelBrazo[]
  readonly enCurso: boolean
  readonly error: string | null
  readonly onCancelar: () => void
  readonly onAgregar: (name: string, language: string) => void
}): JSX.Element {
  const [elegida, setElegida] = useState<string | null>(null)

  /**
   * ⭐ `CA-30` · **Escape = «Cancelar», y llama al MISMO manejador.**
   *
   * ⛔ Hay que escribirlo acá y no se hereda: el popup de quitar lo trae de
   * `Dialogo`, y este panel **no usa `Dialogo` a propósito** (ver el bloque de
   * `role="dialog"` más abajo: `CA-18` pide que la lista se siga viendo detrás).
   * Lo que no se duplica es la CONDUCTA: las dos salidas llaman a `onCancelar`, que
   * es lo único que vuelve a `…/plantillas` sin `POST`. Dos manejadores con dos
   * cuerpos serían dos caminos que pueden divergir, y el que divergiera en silencio
   * sería el de teclado, que nadie mira.
   *
   * ⚠️ Va en `document` y no en el `<aside>` porque el foco puede estar en
   * cualquier control de adentro —o en ninguno, si el panel se abrió por URL—.
   */
  useEffect(() => {
    const alPresionar = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") onCancelar()
    }
    document.addEventListener("keydown", alPresionar)
    return () => document.removeEventListener("keydown", alPresionar)
  }, [onCancelar])

  const plantilla = ofrecidas.find((una) => terna(una.name, una.language) === elegida) ?? null

  return (
    /*
     * ⚠️ `role="dialog"` y NO `Dialogo`: el panel tiene que **dejar ver la lista
     * de atrás** (`CA-18`), y `Dialogo` pinta un overlay opaco y declara
     * `aria-modal="true"`, que le dice al lector de pantalla que lo de atrás no
     * existe. Son dos patrones distintos y el PRD pide éste; el popup de quitar
     * sí usa `Dialogo`, porque ahí sí hay que bloquear.
     */
    <aside
      className="plantillas__panel"
      role="dialog"
      aria-label="Agregar plantilla"
      aria-modal="false"
    >
      <h2 className="plantillas__panel-titulo">Agregar plantilla</h2>

      {/*
        ⭐ `CA-20` · la ayuda dice las TRES restas del bloque ② en una frase, y
        termina con la que nadie adivina: **según la última sincronización**.
      */}
      <p className="plantillas__panel-ayuda">
        Aprobadas en Meta para {sello} que todavía no están en «Plantillas para envíos» de este
        brazo. Según la última sincronización.
      </p>

      {ofrecidas.length === 0 && (
        <p className="plantillas__vacio" role="status">
          No hay plantillas aprobadas para agregar. Si creaste una en Meta, sincroniza primero.
        </p>
      )}

      {ofrecidas.length > 0 && (
        <ul className="plantillas__opciones">
          {ofrecidas.map((una) => {
            const clave = terna(una.name, una.language)
            return (
              <li key={clave} className="plantillas__opcion">
                <label>
                  <input
                    type="radio"
                    name="plantilla-a-agregar"
                    value={clave}
                    checked={elegida === clave}
                    onChange={() => setElegida(clave)}
                  />
                  {/*
                    ⭐ `CA-25` · nombre **y** categoría. El nombre solo no alcanza:
                    los nombres de Meta son identificadores (`cohorte_hibrido`) y
                    la categoría es lo que dice para qué sirve.
                  */}
                  <span>{una.name}</span>
                  <span>{categoriaEnEspanol(una.category)}</span>
                </label>
              </li>
            )
          })}
        </ul>
      )}

      {/*
        ⭐ `CA-27` · al elegir, la confirmación muestra **lo mismo que la tabla va a
        mostrar**: categoría, idioma, variables y botones. Es lo que deja ver, ANTES
        de agregar, que la plantilla elegida es la que se creía.
      */}
      {plantilla !== null && (
        <dl className="plantillas__detalle">
          <dt>Categoría</dt>
          <dd>{categoriaEnEspanol(plantilla.category)}</dd>
          <dt>Idioma</dt>
          <dd>{plantilla.language}</dd>
          <dt>Estado en Meta</dt>
          <dd>{estadoEnEspanol(plantilla.approvalStatus)}</dd>
          <dt>Variables</dt>
          <dd>
            {plantilla.variables.length === 0 ? (
              "Sin variables"
            ) : (
              <ul className="plantillas__variables">
                {variablesParaMostrar(plantilla).map((variable) => (
                  <li key={variable}>{variable}</li>
                ))}
              </ul>
            )}
          </dd>
          <dt>Botones</dt>
          <dd>
            {plantilla.buttons.length === 0 ? (
              "Esta plantilla no trajo botones."
            ) : (
              <ol className="plantillas__botones">
                {plantilla.buttons.map((boton) => (
                  <li key={boton.position} className="plantillas__boton">
                    {botonParaMostrar(boton)}
                  </li>
                ))}
              </ol>
            )}
          </dd>
        </dl>
      )}

      {/*
        ⛔ El rechazo del servidor (404/409 de `R2-AW1`) se dice ACÁ y el panel NO
        se cierra: cerrarlo mandaría al operador a una lista donde su plantilla no
        está, sin nada que explique por qué.
      */}
      {error !== null && (
        <p className="plantillas__error" role="alert">
          {error}
        </p>
      )}

      <footer className="dialogo__acciones">
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className="boton boton--primario"
          /*
           * ⭐ `CA-26` y `CA-31` a la vez. Ver el bloque ③: son dos motivos
           * distintos para el mismo atributo, y el de `CA-31` además tiene texto.
           */
          disabled={plantilla === null || enCurso}
          aria-busy={enCurso}
          onClick={() => {
            if (plantilla !== null) onAgregar(plantilla.name, plantilla.language)
          }}
        >
          Agregar plantilla
        </button>
      </footer>
    </aside>
  )
}
